// hooks/useInscription.ts
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { message, notification } from "antd";
import dayjs from "dayjs";
import { FormData, InscriptionState } from "../types/inscription";
import { inscriptionApi } from "../services/inscriptionApi";
import { validateStep, checkAutoProgression } from "../utils/validation";
import { useNavigate } from "react-router-dom";

const initialFormData: FormData = {
  fullName: "",
  birthDate: dayjs("2000-01-01"),
  gender: "",
  phoneNumber: "",
  email: "",
  verificationCode: "",
  password: "",
  confirmPassword: "",
  termsAccepted: false, // NEW: Initialize terms acceptance
};

export const useInscription = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [state, setState] = useState<InscriptionState>({
    currentStep: 0,
    loading: false,
    formData: initialFormData,
    isOtpVerified: false,
    otpToken: "",
    resendCooldown: 0,
    passwordStrength: 0,
    showPassword: false,
    autoProgressTimer: null,
    actualOTP: "",
  });

  // Reset OTP verification when phone number changes
  useEffect(() => {
    if (state.formData.phoneNumber.length === 8) {
      setState((prev) => ({ ...prev, isOtpVerified: false }));
    }
  }, [state.formData.phoneNumber]);

  // Clear timer on unmount
  useEffect(() => {
    return () => {
      if (state.autoProgressTimer) {
        clearTimeout(state.autoProgressTimer);
      }
    };
  }, [state.autoProgressTimer]);

  function delay(ms: number) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  const updateFormField = (field: keyof FormData, value: any) => {
    setState((prev) => {
      const newFormData = { ...prev.formData, [field]: value };

      // Reset OTP verification if phone number changes
      const updates: Partial<InscriptionState> = {
        formData: newFormData,
      };

      if (field === "phoneNumber") {
        updates.isOtpVerified = false;
      }

      // Check for auto-progression
      const shouldProgress = checkAutoProgression(
        prev.currentStep,
        newFormData
      );
      if (shouldProgress && prev.currentStep < 4) {
        // Updated to 4 for new step count
        // Clear existing timer
        if (prev.autoProgressTimer) {
          clearTimeout(prev.autoProgressTimer);
        }

        // Set new timer for auto-progression
        const timer = setTimeout(() => {
          // Auto progress implementation can be added here if needed
        }, 1500);

        updates.autoProgressTimer = timer;
      }

      return { ...prev, ...updates };
    });
  };

  // add inside useInscription (near other actions)
  const acceptTermsAndProceed = () => {
    setState((prev) => ({
      ...prev,
      formData: { ...prev.formData, termsAccepted: true },
      // advance step atomically (keep validation logic centralized)
      currentStep:
        prev.currentStep < 4 ? prev.currentStep + 1 : prev.currentStep,
    }));
  };

  const sendVerificationCode = async () => {
    setState((prev) => ({ ...prev, loading: true }));

    try {
      const result = await inscriptionApi.sendVerificationCode(
        state.formData.phoneNumber
      );

      const token = typeof result === "string" ? result : result.token;
      const actualOTP = typeof result === "object" ? result.otp : null;

      setState((prev) => ({
        ...prev,
        otpToken: token,
        currentStep: prev.currentStep + 1,
        actualOTP: actualOTP ?? "",
        loading: false,
      }));
    } catch (error) {
      setState((prev) => ({ ...prev, loading: false }));
      throw error;
    }
  };

  const verifyOtpCode = async (code: string) => {
    setState((prev) => ({ ...prev, loading: true }));

    try {
      const isVerified = await inscriptionApi.verifyOtpCode(
        state.otpToken,
        code
      );
      setState((prev) => ({
        ...prev,
        isOtpVerified: isVerified,
        loading: false,
        currentStep: isVerified ? prev.currentStep + 1 : prev.currentStep,
      }));
    } catch (error) {
      message.error("Entre valid OTP");
      setState((prev) => ({ ...prev, loading: false }));
    }
  };

  const sendEmailOTP = async (
    email: string,
    phoneNumber: string,
    otp: string
  ) => {
    console.log("email", email);
    console.log("phoneNumber", phoneNumber);
    console.log("otp", otp);
    setState((prev) => ({ ...prev, loading: true }));

    try {
      await inscriptionApi.sendEmailOTP(email, phoneNumber, otp);

      notification.success({
        message: t("inscription.notifications.otpSent"),
        description: t("inscription.notifications.emailOtpSentSuccess", {
          email,
        }),
        placement: "topRight",
      });

      return true;
    } catch (err) {
      notification.error({
        message: t("inscription.notifications.emailOtpSendFailed"),
        description:
          t("inscription.notifications.emailOtpSendFailedDescription") ||
          "Échec de l'envoi du code par email",
        placement: "topRight",
      });

      return false;
    } finally {
      setState((prev) => ({ ...prev, loading: false }));
    }
  };

  const registerPatient = async () => {
    setState((prev) => ({ ...prev, loading: true }));

    try {
      const payload = {
        fullName: state.formData.fullName,
        birthDate: state.formData.birthDate.format("YYYY-MM-DD"),
        gender: state.formData.gender.toUpperCase(),
        password: state.formData.password,
        phoneNumber: state.formData.phoneNumber,
        enterpriseId: localStorage.getItem("enterpriseId"),
      };
      console.log("first payload", payload);
      await inscriptionApi.registerPatient(payload);

      await delay(1500);

      navigate("/inscription/success");
      // Reset form after successful registration
      setState((prev) => ({
        ...prev,
        formData: initialFormData,
        currentStep: 0,
        loading: false,
        isOtpVerified: false,
        otpToken: "",
      }));
    } catch (error) {
      console.log("Error registering patient:", error);
      setState((prev) => ({ ...prev, loading: false }));
    }
  };

  const handleNext = async () => {
    if (state.loading) return;

    const validation = validateStep(state.currentStep, state.formData, t);
    if (!validation.isValid) {
      setErrors(validation.errors);
      // show a generic first error in the toast if you want
      const firstMsg = Object.values(validation.errors)[0];
      message.error(firstMsg);
      return;
    }
    setErrors({}); // clear previous

    if (state.currentStep === 3 && !state.isOtpVerified) {
      // Updated step number
      message.error(
        t("inscription.notifications.otpRequired") ||
          "Please verify your OTP code"
      );
      return;
    }

    if (state.currentStep < 4) {
      // Updated to 4 for last step
      setState((prev) => ({ ...prev, currentStep: prev.currentStep + 1 }));
      return;
    }

    try {
      console.log("first payload befoofo", state.formData);
      setState((prev) => ({ ...prev, loading: true }));
      await registerPatient();
    } catch (error) {
      console.error("Patient registration failed:", error);
    } finally {
      setState((prev) => ({ ...prev, loading: false }));
    }
  };

  const handleFieldChange = (field: keyof FormData, value: any) => {
    setState((prev) => ({
      ...prev,
      formData: { ...prev.formData, [field]: value },
    }));
    setErrors((prev) => {
      const copy = { ...prev };
      delete copy[field];
      return copy;
    });
  };

  const handlePrev = () => {
    // Clear auto-progress timer when going back
    if (state.autoProgressTimer) {
      clearTimeout(state.autoProgressTimer);
      setState((prev) => ({ ...prev, autoProgressTimer: null }));
    }

    if (state.currentStep > 0) {
      setState((prev) => ({ ...prev, currentStep: prev.currentStep - 1 }));
    }
  };

  return {
    // State
    currentStep: state.currentStep,
    loading: state.loading,
    formData: state.formData,
    isOtpVerified: state.isOtpVerified,
    passwordStrength: state.passwordStrength,
    showPassword: state.showPassword,
    actualOTP: state.actualOTP,
    // Actions
    updateFormField,
    sendVerificationCode,
    verifyOtpCode,
    sendEmailOTP,
    handleNext,
    handlePrev,
    acceptTermsAndProceed,

    // Utilities
    t,
  };
};
